import { useMemo, useState } from "react";
import { useLoaderData, useNavigate, useNavigation, useSubmit } from "react-router-dom";

import { useAuthStore } from "src/components/main/authStore";
import {
    buildWeekNumArray,
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    getDateTime,
    localWeekDays,
    MyDate,
    outsideHolidays,
    outsidePotDefaultEmptyData,
    simpleStatusColorCode,
    standbyStartDate,
    weeknum,
} from "src/components/common/functions";

type PotDay = {
    date: string;
    inYard: string;
    outYard: string;
    weekDay: string;
    yard: string;
};

type PotFormData = {
    form: PotDay[];
    status: string;
    unlock?: boolean;
};

type DivisionOption = {
    division: string;
};

type LoaderData = {
    divisions: DivisionOption[];
    form: PotFormData;
    week: string;
};

const title = "PlannedOT";
const getStatusColor = (status: string) => (simpleStatusColorCode as Record<string, string>)[status] ?? "#6c757d";
const defaultForm = JSON.parse(outsidePotDefaultEmptyData) as PotFormData;

export default function PlannedOT() {
    const { week, form: loadedForm, divisions } = useLoaderData() as LoaderData;
    const user = useAuthStore(state => state.user);
    const navigate = useNavigate();
    const navigation = useNavigation();
    const submit = useSubmit();
    const [form, setForm] = useState<PotFormData>(loadedForm);
    const [isEditingContent, setIsEditingContent] = useState(false);
    const isSubmitting = navigation.state === "submitting";
    const hireDateString = user?.hireDate ?? "";
    const quitDateString = user?.quitDate ?? "";

    const weeks = useMemo(
        () => buildWeekNumArray(standbyStartDate).filter((value) => (
            getDateTime(value, 3, 0, 0) > new Date(hireDateString.replace(/-/g, "/")) &&
            (quitDateString ? getDateTime(value, 3, 0, 0) <= new Date(quitDateString.replace(/-/g, "/")) : true)
        )).reverse(),
        [hireDateString, quitDateString]
    );

    const activeWeek = weeks.includes(week)
        ? week
        : (weeks.includes(weeknum(new MyDate().s())) ? weeknum(new MyDate().s()) : (weeks[0] || ""));

    const timeValid = (weekValue: string) => getDateTime(weekValue, 3, 11, 0) > new Date();
    const currentForm = form.form.length ? form : loadedForm;

    const visibleDays = useMemo(
        () => localWeekDays
            .map((name, index) => ({ name, index }))
            .filter((item) => {
                const holidayDates = outsideHolidays(Number(activeWeek.substring(1).split("-")[0])).map((holiday) => holiday.date);
                const currentDate = getDatefromWeekNum(activeWeek, item.index + 4);
                return (currentDate ? holidayDates.includes(currentDate) : false) || item.index === 1 || item.index === 2;
            }),
        [activeWeek]
    );

    const updateDay = (dayIndex: number, updater: (day: PotDay) => PotDay) => {
        setForm((current) => ({
            ...current,
            form: current.form.map((day, index) => index === dayIndex ? updater(day) : day),
        }));
    };

    const submitForm = (nextForm: PotFormData) => {
        const formData = new FormData();
        formData.append("currentWeek", activeWeek);
        formData.append("data", JSON.stringify(nextForm));
        submit(formData, { method: "post" });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "plannedOTWeek">Week</label>
                    <select
                        id = "plannedOTWeek"
                        className = "form-control w-auto"
                        value = {activeWeek}
                        onChange = {e => {
                            setIsEditingContent(false);
                            setForm(loadedForm);
                            navigate(`/${title}/${e.currentTarget.value}`);
                        }}
                    >
                        {weeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </div>
            </nav>
            <div className = "p-3">
                <div className = "cssSeperateWhenWide align-items-center">
                    <div className = "h3 text-danger fw-bold mb-0">{getDateEndFromWeekNum(activeWeek)}</div>
                    <div className = "h4 mb-0 ms-2" style = {{ color: getStatusColor(currentForm.status) }}>{currentForm.status}</div>
                    {!timeValid(activeWeek) && !currentForm.unlock ? <span className = "ms-2 text-light fw-bold bg-secondary rounded text-nowrap px-2 py-1">past</span> : null}
                </div>
                <div className = "row mt-2">
                    {visibleDays.map((weekday) => (
                        <div className = "col-lg" key = {weekday.index}>
                            <div className = "h4 text-secondary">{weekday.name} ({getDatefromWeekNum(activeWeek, weekday.index + 4)})</div>
                            <div className = "mt-2">
                                <label htmlFor = {`self-${weekday.index}`} className = "fw-bold mb-0">Working OT at assigned yard:</label>
                                {isEditingContent ? (
                                    <select
                                        id = {`self-${weekday.index}`}
                                        className = "form-control"
                                        required
                                        value = {currentForm.form[weekday.index]?.inYard ?? ""}
                                        onChange = {e => {
                                            const value = e.currentTarget.value;
                                            updateDay(weekday.index, (day) => ({
                                                ...day,
                                                inYard: value,
                                                yard: value === "yes" ? day.inYard : "",
                                                weekDay: localWeekDays[weekday.index],
                                                date: getDatefromWeekNum(activeWeek, weekday.index + 4) ?? "",
                                            }));
                                        }}
                                    >
                                        <option value = "">Select</option>
                                        <option>Yes</option>
                                        <option>No</option>
                                    </select>
                                ) : <span className = "ms-2">{currentForm.form[weekday.index]?.inYard}</span>}
                            </div>
                            <div className = "mt-2">
                                <label htmlFor = {`another-${weekday.index}`} className = "fw-bold mb-0">Working OT to assist another yard:</label>
                                {isEditingContent ? (
                                    <div>
                                        <select
                                            id = {`another-${weekday.index}`}
                                            className = "form-control"
                                            required
                                            value = {currentForm.form[weekday.index]?.outYard ?? ""}
                                            onChange = {e => {
                                                const value = e.currentTarget.value;
                                                updateDay(weekday.index, (day) => ({ ...day, outYard: value }));
                                            }}
                                        >
                                            <option value = "">Select</option>
                                            <option>Yes</option>
                                            <option>No</option>
                                        </select>
                                    </div>
                                ) : <span className = "ms-2">{currentForm.form[weekday.index]?.outYard}</span>}
                            </div>
                        </div>
                    ))}
                </div>
                <div className = "mt-2">
                    <label htmlFor = "yard" className = "fw-bold mb-0">Yard:</label>
                    {isEditingContent ? (
                        <select
                            id = "yard"
                            className = "form-control w-auto"
                            required
                            value = {currentForm.form[0]?.yard ?? ""}
                            onChange = {e => {
                                const value = e.currentTarget.value;
                                updateDay(0, (day) => ({ ...day, yard: value }));
                            }}
                        >
                            <option value = "">Select</option>
                            {divisions.map((division, index) => <option key = {index}>{division.division}</option>)}
                        </select>
                    ) : <span className = "ms-2">{currentForm.form[0]?.yard}</span>}
                </div>
                {currentForm.status === "Submitted" || (!timeValid(activeWeek) && !currentForm.unlock) ? null : (
                    <div className = "text-right">
                        {isEditingContent ? (
                            <>
                                <button
                                    type = "button"
                                    className = "btn btn-primary mx-3"
                                    disabled = {isSubmitting}
                                    onClick = {() => {
                                        const nextForm = { ...currentForm, status: "Saved" };
                                        submitForm(nextForm);
                                        setIsEditingContent(false);
                                    }}
                                >
                                    {isSubmitting ? "Saving..." : "Save Content"}
                                </button>
                                <button
                                    type = "button"
                                    className = "btn btn-primary mx-3"
                                    disabled = {isSubmitting}
                                    onClick = {() => {
                                        if (!window.confirm("Are you sure to submit for the week?")) return;
                                        const nextForm = { ...currentForm, status: "Submitted" };
                                        submitForm(nextForm);
                                        setIsEditingContent(false);
                                    }}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit For the Week"}
                                </button>
                            </>
                        ) : (
                            <button
                                type = "button"
                                className = "btn btn-primary mx-3"
                                onClick = {e => {
                                    e.preventDefault();
                                    setForm(loadedForm.form.length ? loadedForm : defaultForm);
                                    setIsEditingContent(true);
                                }}
                            >
                                Edit Content
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
